import { DateRange, FilterOpts } from "../OrderListPage/OrderListPage";
import { models } from "types/api/viewModels.ts";
import { getSingleOrderGlobal } from "./OrderDetailUtil.ts";
import { PostOrdersRequestBody } from "types/api/orders/postOrders.ts";
import { getOrderTypeDisplayLabel } from "utils/order";

const getOrderValue = (item: models["OrderViewModel"] | null | undefined, key: FilterValues): string => {
	switch (key) {
		case "account":
			return item?.accountNumber ?? "";
		case "designerNumber":
			return item?.designer?.designerNumber ?? "Designer not identified";
		case "brand":
			return getSingleOrderGlobal(item)?.brand?.description?.replace(/_/g, " ") ?? "";
		case "status":
			return item?.status?.replace(/_/g, " ") ?? "";
		case "orderType":
			return getOrderTypeDisplayLabel(item?.orderType) ?? "";
		default:
			return "";
	}
};

const getCleanFilterValues = (key: string, selectedFilterValues: string[]): string[] => {
	return selectedFilterValues.map((fv: string) => {
		if (key === "account") {
			fv = fv.split(" - ")[0]?.trim() ?? "";
		} else if (key === "designerNumber") {
			if (fv.includes("-")) {
				fv = fv.split("-")[1] ?? "";
			}
			fv = fv.trim();
		}

		return fv.toUpperCase();
	});
};

export type FilterValues = "brand" | "account" | "designerNumber" | "status" | "orderType";

export const getFilteredOrders = (
	filterOptions: FilterOpts,
	orders: models["OrderViewModel"][] | null | undefined
): models["OrderViewModel"][] => {
	const { brands, accounts, designers, status, types } = filterOptions;

	const compare = (key: FilterValues, selectedFilterValues: string[], item: models["OrderViewModel"]) => {
		const cleanFilterValues = getCleanFilterValues(key, selectedFilterValues);
		return cleanFilterValues.includes(getOrderValue(item, key).toUpperCase());
	};

	const activeFilters = (
		Object.entries({
			brand: brands,
			account: accounts,
			designerNumber: designers,
			status: status,
			orderType: types
		}) as [FilterValues, string[] | undefined][]
	)
		.map(
			([key, values]) =>
				[key, (values ?? []).filter((value) => value?.trim().length > 0)] as [FilterValues, string[]]
		)
		.filter(([, values]) => values.length > 0);

	return activeFilters.reduce<models["OrderViewModel"][]>(
		(acc, [key, values]) => acc.filter((item) => compare(key, values, item)),
		orders ?? []
	);
};

type DatetimeObject = {
	fromDate: undefined | Date;
	toDate: undefined | Date;
};

export const objectToDateRange = (t: DatetimeObject): DateRange =>
	t?.fromDate === undefined || t?.toDate === undefined
		? t
		: {
				fromDate: new Date(t?.fromDate),
				toDate: new Date(t?.toDate)
			};

export function removeImportErrorsFromOrder(order: PostOrdersRequestBody | undefined): PostOrdersRequestBody {
	const cleanedOrder = { ...order };

	if (!cleanedOrder.configurations) return cleanedOrder;

	cleanedOrder.configurations = cleanedOrder.configurations.map((config) => {
		const newConfig = { ...config };

		if (!newConfig.lineItems) return newConfig;

		newConfig.lineItems = newConfig.lineItems.map((item) => {
			const newItem = { ...item, importError: undefined };

			if (newItem.modifications) {
				newItem.modifications = newItem.modifications.map((mod) => ({
					...mod,
					importError: undefined
				}));
			}

			return newItem;
		});

		return newConfig;
	});

	return cleanedOrder;
}

export const removeImportErrorFromLineItem = (
	updatedItems: models["PendingOrderConfigurationViewModel"][] | undefined | null
) => {
	if (!updatedItems) return;

	const copiedItems = updatedItems.map((config) => ({ ...config }));

	return copiedItems.map((config) => {
		if (!config.lineItems) return config;

		const updatedLineItems = config.lineItems.map((lineItem) => ({
			...lineItem,
			importError: undefined,
			modifications: lineItem.modifications?.map((mod) => ({
				...mod,
				importError: undefined
			}))
		}));

		return {
			...config,
			lineItems: updatedLineItems
		};
	});
};
